import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import MenuItem from "@material-ui/core/MenuItem";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import RefreshIcon from "@material-ui/icons/Refresh";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import TelegramIcon from "@material-ui/icons/Telegram";
import LanguageIcon from "@material-ui/icons/Language";
import EmojiObjectsOutlinedIcon from "@material-ui/icons/EmojiObjectsOutlined";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import ReceiptOutlinedIcon from "@material-ui/icons/ReceiptOutlined";
import BuildOutlinedIcon from "@material-ui/icons/BuildOutlined";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import WarningIcon from "@material-ui/icons/Warning";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useDialogStyles } from "../../theme/redesign";
import { DialogClose, FilterChips, KpiTile, PageFrame, RecordsState, SearchField } from "./shared";
import { useStyles as useNegocioStyles } from "./styles";
import { money, accountIsFullSale } from "./helpers";
import { statusLabel } from "../Storefront/helpers";
import { needsSlots } from "./catalog";
import { activationAccounts, activationProfiles } from "./storeActivation";
import { AccountOption } from "./AccountPeek";
import { useCustomerChat } from "./CustomerChatButton";

const useStyles = makeStyles((theme) => ({
  "@keyframes ordSpin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
  "@keyframes ordPulse": {
    "0%, 100%": { transform: "scale(1)", boxShadow: "0 0 0 0 rgba(37, 211, 102, .45)" },
    "50%": { transform: "scale(1.08)", boxShadow: "0 0 0 6px rgba(37, 211, 102, 0)" }
  },
  "@keyframes ordFly": {
    "0%, 100%": { transform: "translate(0, 0) rotate(0deg)" },
    "50%": { transform: "translate(2px, -3px) rotate(-8deg)" }
  },
  "@keyframes ordGlow": {
    "0%, 100%": { transform: "translateY(0)", filter: "drop-shadow(0 0 0 rgba(139, 92, 246, 0))" },
    "50%": { transform: "translateY(-2px)", filter: "drop-shadow(0 0 5px rgba(139, 92, 246, .8))" }
  },
  origin: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "3px 10px 3px 4px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: "nowrap",
    border: "1px solid transparent"
  },
  originIcon: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    "& svg": { fontSize: 15 },
    "@media (prefers-reduced-motion: reduce)": { animation: "none !important" }
  },
  web: { background: "rgba(14, 165, 233, .12)", color: "#0369a1", borderColor: "rgba(14, 165, 233, .35)" },
  webIcon: { background: "#0ea5e9", "& svg": { animation: "$ordSpin 6s linear infinite" } },
  whatsapp: { background: "rgba(37, 211, 102, .12)", color: "#047857", borderColor: "rgba(37, 211, 102, .4)" },
  whatsappIcon: { background: "#25d366", animation: "$ordPulse 2s ease-in-out infinite" },
  telegram: { background: "rgba(42, 171, 238, .12)", color: "#0b6aa2", borderColor: "rgba(42, 171, 238, .4)" },
  telegramIcon: { background: "#2aabee", "& svg": { animation: "$ordFly 1.8s ease-in-out infinite" } },
  agent: { background: "rgba(139, 92, 246, .12)", color: "#6d28d9", borderColor: "rgba(139, 92, 246, .4)" },
  agentIcon: { background: "linear-gradient(135deg, #8b5cf6, #ec4899)", animation: "$ordGlow 2.2s ease-in-out infinite" },
  panel: { background: "rgba(100, 116, 139, .12)", color: "#334155", borderColor: "rgba(100, 116, 139, .35)" },
  panelIcon: { background: "#64748b" },
  subChannel: { display: "inline-flex", marginLeft: 2, "& svg": { fontSize: 14 } },
  alert: {
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
    padding: "12px 14px",
    borderRadius: 12,
    marginBottom: 16,
    background: theme.palette.type === "dark" ? "rgba(245, 158, 11, .14)" : "#fffbeb",
    border: "1px solid rgba(245, 158, 11, .45)",
    color: theme.palette.type === "dark" ? "#fcd34d" : "#92400e",
    fontSize: 13,
    "& p": { margin: "2px 0" }
  },
  toolbar: { display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", margin: "8px 0 14px" },
  list: { display: "grid", gap: 10 },
  card: {
    display: "grid",
    gridTemplateColumns: "minmax(170px, auto) 1fr auto",
    gap: 14,
    alignItems: "center",
    padding: "12px 14px",
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" }
  },
  cardMain: { minWidth: 0, display: "grid", gap: 2, "& strong": { fontSize: 14 }, "& span": { fontSize: 12.5, color: theme.palette.text.secondary } },
  cardSide: { display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "flex-end", alignItems: "center" },
  amount: { fontWeight: 700, fontSize: 15, marginRight: 6 },
  detailGrid: {
    display: "grid",
    gridTemplateColumns: "140px 1fr",
    gap: "6px 12px",
    fontSize: 13.5,
    "& dt": { color: theme.palette.text.secondary },
    "& dd": { margin: 0, wordBreak: "break-word" }
  },
  receiptBox: {
    marginTop: 16,
    borderRadius: 12,
    border: `1px dashed ${theme.palette.divider}`,
    padding: 10,
    textAlign: "center",
    "& img": { maxWidth: "100%", maxHeight: 520, borderRadius: 8 },
    "& iframe": { width: "100%", height: 520, border: 0 }
  },
  more: { display: "flex", justifyContent: "center", marginTop: 14 }
}));

const STATUS_FILTERS = [
  { value: "pending", label: "Por atender" },
  { value: "review", label: "Pago en revisión" },
  { value: "done", label: "Entregados" },
  { value: "cancelled", label: "Cancelados" },
  { value: "all", label: "Todos" }
];

const ORIGIN_FILTERS = [
  { value: "", label: "Todos los orígenes" },
  { value: "web", label: "Tienda web" },
  { value: "bot", label: "Bot" },
  { value: "agent", label: "Agente IA" }
];

const statusColor = (status) =>
  status === "payment_review" ? "#d97706" : status === "awaiting_payment" ? "#64748b" : status === "manual_activation" ? "#7c3aed" :
  status === "special_request" ? "#0ea5e9" : ["fulfilled", "paid"].includes(status) ? "#16a34a" : "#94a3b8";

// Sin pago confirmado: el admin puede cancelarlos (pruebas, clientes que desisten).
const canCancel = (order) => ["awaiting_payment", "payment_review"].includes(order?.status);

const channelIcon = (channel) => (channel === "telegram" ? <TelegramIcon /> : <WhatsAppIcon />);
const channelName = (channel) => (channel === "telegram" ? "Telegram" : channel === "web" ? "Web" : "WhatsApp");

/** De dónde viene el pedido, con ícono animado: 🌐 tienda · 🟢 bot WhatsApp · ✈️ bot Telegram · ✨ agente IA. */
export function OriginBadge({ order }) {
  const c = useStyles();
  const { origin, channel } = order;
  if (origin === "web") {
    return <span className={`${c.origin} ${c.web}`}><span className={`${c.originIcon} ${c.webIcon}`}><LanguageIcon /></span>Tienda web</span>;
  }
  if (origin === "agent") {
    return (
      <Tooltip title={`El agente IA atendió la compra por ${channelName(channel)}`}>
        <span className={`${c.origin} ${c.agent}`}>
          <span className={`${c.originIcon} ${c.agentIcon}`}><EmojiObjectsOutlinedIcon /></span>
          Agente IA<span className={c.subChannel}>{channel === "web" ? <LanguageIcon /> : channelIcon(channel)}</span>
        </span>
      </Tooltip>
    );
  }
  if (origin === "panel") {
    return <span className={`${c.origin} ${c.panel}`}><span className={`${c.originIcon} ${c.panelIcon}`}><PersonOutlineIcon /></span>Panel</span>;
  }
  if (channel === "telegram") {
    return <span className={`${c.origin} ${c.telegram}`}><span className={`${c.originIcon} ${c.telegramIcon}`}><TelegramIcon /></span>Bot · Telegram</span>;
  }
  if (channel === "web") {
    return <span className={`${c.origin} ${c.web}`}><span className={`${c.originIcon} ${c.webIcon}`}><LanguageIcon /></span>Chat de la tienda</span>;
  }
  return <span className={`${c.origin} ${c.whatsapp}`}><span className={`${c.originIcon} ${c.whatsappIcon}`}><WhatsAppIcon /></span>Bot · WhatsApp</span>;
}

const fmtDate = (value) => {
  if (!value) return "—";
  const d = new Date(String(value).replace(" ", "T").replace(/\.\d+$/, "") + (String(value).includes("Z") ? "" : "Z"));
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString("es", { dateStyle: "medium", timeStyle: "short" });
};

const orderMoney = (o) => money(o.amount, o.currency && o.currency !== "local" ? o.currency : undefined);

function ReceiptView({ order }) {
  const c = useStyles();
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    let url = null;
    let alive = true;
    if (!order.hasReceipt) {
      setState({ loading: false });
      return undefined;
    }
    api
      .get(`/negocio/orders/${order.id}/receipt`, { responseType: "blob" })
      .then(({ data }) => {
        url = URL.createObjectURL(data);
        if (alive) setState({ loading: false, url, pdf: /pdf/i.test(data.type || "") });
      })
      .catch(() => alive && setState({ loading: false, error: true }));
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [order.id, order.hasReceipt]);
  if (state.loading) return <div className={c.receiptBox}>Cargando comprobante…</div>;
  if (state.url) {
    return (
      <div className={c.receiptBox}>
        {state.pdf ? <iframe title="Comprobante" src={state.url} /> : <img src={state.url} alt="Comprobante de pago" />}
        <div><Button size="small" component="a" href={state.url} target="_blank" rel="noopener noreferrer">Abrir en otra pestaña</Button></div>
      </div>
    );
  }
  return (
    <div className={c.receiptBox}>
      {order.noPhoto
        ? "Sin foto: el cliente indicó que ya pagó, pero no envió comprobante. Verifica el pago en tu banco o pídele la captura por el chat."
        : state.error
          ? "No se pudo cargar el comprobante."
          : "Este pedido no tiene comprobante."}
    </div>
  );
}

function OrderDetail({ order, busy, onClose, onReview, onOpenChat, onActivate, onCloseSpecial, onCancel }) {
  const c = useStyles();
  const dlg = useDialogStyles();
  if (!order) return null;
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth classes={{ paper: dlg.paper }}>
      <DialogClose onClose={onClose} />
      <DialogTitle>Pedido #{order.id}</DialogTitle>
      <DialogContent dividers>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
          <OriginBadge order={order} />
          <Chip size="small" label={statusLabel(order.status)} style={{ background: statusColor(order.status), color: "#fff" }} />
          {order.cancelledBy === "customer" && <Chip size="small" variant="outlined" label="Cancelado por el cliente" />}
          {order.cancelledBy === "admin" && <Chip size="small" variant="outlined" label="Cancelado por el admin" />}
        </div>
        <dl className={c.detailGrid}>
          <dt>Pedido</dt><dd>{order.title}</dd>
          {order.items?.length > 1 || (order.items?.[0] && order.items[0].name !== order.title) ? (
            <><dt>Productos</dt><dd>{order.items.map((i) => `${i.name} × ${i.quantity}`).join(", ")}</dd></>
          ) : null}
          <dt>Total</dt><dd><strong>{orderMoney(order)}</strong></dd>
          <dt>Método de pago</dt><dd>{order.paymentMethod || "—"}</dd>
          <dt>Cliente</dt><dd>{order.name || "—"}{order.number ? ` · ${order.number}` : ""}</dd>
          {order.memberEmail && <><dt>Correo del cliente</dt><dd>{order.memberEmail}</dd></>}
          {order.externalId && <><dt>ID externo</dt><dd>{order.externalId}</dd></>}
          {order.customerDetails && <><dt>Indicaciones</dt><dd>{order.customerDetails}</dd></>}
          <dt>Creado</dt><dd>{fmtDate(order.createdAt)}</dd>
          {order.paidAt && <><dt>Pagado</dt><dd>{fmtDate(order.paidAt)}</dd></>}
          {order.fulfilledAt && <><dt>Entregado</dt><dd>{fmtDate(order.fulfilledAt)}</dd></>}
          {order.saleId && <><dt>Venta</dt><dd>#{order.saleId}</dd></>}
          {order.ticketId && <><dt>Chat</dt><dd>Ticket #{order.ticketId}</dd></>}
          {order.rejected && <><dt>Rechazo</dt><dd>{order.rejected}</dd></>}
        </dl>
        <ReceiptView order={order} />
      </DialogContent>
      <DialogActions style={{ flexWrap: "wrap", gap: 6 }}>
        {order.contactId && <Button disabled={busy} onClick={() => onOpenChat(order)}>Abrir chat</Button>}
        {order.status === "manual_activation" && order.channel === "web" && <Button color="primary" disabled={busy} onClick={() => onActivate(order)}>Completar activación</Button>}
        {order.status === "special_request" && <Button disabled={busy} onClick={() => onCloseSpecial(order)}>Cerrar pedido especial</Button>}
        {canCancel(order) && <Button disabled={busy} style={{ color: "#dc2626" }} onClick={() => onCancel(order)}>Cancelar pedido</Button>}
        {order.status === "payment_review" && (
          <>
            <Button disabled={busy} onClick={() => onReview(order, false)}>Rechazar</Button>
            <Button variant="contained" color="primary" disabled={busy} onClick={() => onReview(order, true)}>Aprobar pago</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default function OrdersPage() {
  const c = useStyles();
  const negocio = useNegocioStyles();
  const dlg = useDialogStyles();
  const history = useHistory();
  const [status, setStatus] = useState("pending");
  const [origin, setOrigin] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ records: [], counts: {}, count: 0, hasMore: false });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reach, setReach] = useState(null);
  const [detail, setDetail] = useState(null);
  const [review, setReview] = useState(null);
  const [reason, setReason] = useState("");
  const [closingSpecial, setClosingSpecial] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [notifyCancel, setNotifyCancel] = useState(false);
  const [activation, setActivation] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const { openChat, modal: chatModal } = useCustomerChat();
  const query = useRef({ status, origin, q });
  query.current = { status, origin, q };

  const load = useCallback(async (nextPage = 1, quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const { data: res } = await api.get("/negocio/orders", { params: { ...query.current, page: nextPage } });
      setData((prev) => (nextPage > 1 ? { ...res, records: [...prev.records, ...res.records] } : res));
      setPage(nextPage);
    } catch (err) {
      if (!quiet) toastError(err);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => load(1), q ? 350 : 0);
    return () => clearTimeout(timer);
  }, [status, origin, q, load]);

  useEffect(() => {
    api.get("/negocio/orders/admin-reachability").then(({ data: r }) => setReach(r)).catch(() => undefined);
    const timer = setInterval(() => {
      if (document.visibilityState === "visible" && page === 1) load(1, true);
    }, 30000);
    return () => clearInterval(timer);
  }, [load, page]);

  const run = async (action) => {
    setBusy(true);
    try {
      await action();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const refreshOne = async (id) => {
    const { data: fresh } = await api.get(`/negocio/orders/${id}`);
    setData((prev) => ({ ...prev, records: prev.records.map((r) => (r.id === id ? fresh : r)) }));
    setDetail((d) => (d && d.id === id ? fresh : d));
    return fresh;
  };

  const confirmReview = () =>
    run(async () => {
      const { data: res } = await api.post(`/negocio/orders/${review.order.id}/review`, { approve: review.approve, reason });
      toast.success(res.message);
      setReview(null);
      setDetail(res.order);
      await load(1, true);
    });

  const askCancel = (order) => { setCancelling(order); setNotifyCancel(false); };
  const confirmCancel = () =>
    run(async () => {
      const { data: res } = await api.post(`/negocio/orders/${cancelling.id}/cancel`, { notify: notifyCancel });
      toast.success(res.message);
      setCancelling(null);
      setDetail((d) => (d && d.id === res.order.id ? res.order : d));
      await load(1, true);
    });

  const openActivation = (order) =>
    run(async () => {
      const { data: res } = await api.get("/negocio/accounts");
      setAccounts(res.records || []);
      setActivation(order);
      setDeliveries(order.items.flatMap((item) => Array.from({ length: item.quantity }, () => ({ productId: item.productId, name: item.name, accountId: "", profileId: "", deliveryCode: "" }))));
    });

  const counts = data.counts || {};
  const records = data.records || [];

  return (
    <PageFrame
      title="Pedidos"
      subtitle="Todos los pedidos de la tienda web, los bots de WhatsApp y Telegram y el agente IA, en un solo lugar."
      actions={<Button variant="outlined" startIcon={<RefreshIcon />} disabled={loading} onClick={() => load(1)}>Actualizar</Button>}
    >
      {reach && !reach.reachable && (
        <div className={c.alert} role="alert">
          <WarningIcon />
          <div>
            <p><strong>Nadie recibe los avisos de pagos por validar.</strong></p>
            {reach.issues.map((issue) => <p key={issue}>{issue}</p>)}
            {reach.hint && <p>{reach.hint}</p>}
            <Button size="small" color="primary" onClick={() => history.push("/negocio/configuracion/bot")}>Configurar administrador</Button>
          </div>
        </div>
      )}
      {reach && reach.reachable && reach.issues?.length > 0 && (
        <div className={c.alert} role="status">
          <WarningIcon />
          <div>{reach.issues.map((issue) => <p key={issue}>{issue}</p>)}</div>
        </div>
      )}

      <div className={negocio.kpiGrid}>
        <KpiTile icon={HourglassEmptyIcon} tone="kpiToneWarn" label="Por atender" value={counts.pending ?? "…"} onClick={() => setStatus("pending")} />
        <KpiTile icon={ReceiptOutlinedIcon} tone="kpiToneBad" label="Pago en revisión" value={counts.payment_review ?? 0} onClick={() => setStatus("review")} />
        <KpiTile icon={BuildOutlinedIcon} label="Activación manual" value={counts.manual_activation ?? 0} onClick={() => setStatus("pending")} />
        <KpiTile icon={StarBorderIcon} label="Pedidos especiales" value={counts.special_request ?? 0} onClick={() => setStatus("pending")} />
      </div>

      <div className={c.toolbar}>
        <FilterChips value={status} onChange={setStatus} options={STATUS_FILTERS} />
        <TextField select size="small" variant="outlined" value={origin} onChange={(e) => setOrigin(e.target.value)} style={{ minWidth: 190 }}>
          {ORIGIN_FILTERS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        <SearchField value={q} onChange={setQ} placeholder="Buscar por cliente, número o #pedido" />
      </div>

      <RecordsState loading={loading} count={records.length} emptyTitle="No hay pedidos con estos filtros" emptySubtitle="Los pedidos de la tienda, los bots y el agente IA aparecen aquí apenas se crean.">
        <div className={c.list}>
          {records.map((order) => (
            <article key={order.id} className={c.card}>
              <div><OriginBadge order={order} /></div>
              <div className={c.cardMain}>
                <strong>#{order.id} · {order.title}</strong>
                <span>{order.name || "Cliente"}{order.number ? ` · ${order.number}` : ""}{order.paymentMethod ? ` · ${order.paymentMethod}` : ""}</span>
                <span>{fmtDate(order.createdAt)}{order.noPhoto ? " · sin foto del comprobante" : ""}{order.cancelledBy === "customer" ? " · cancelado por el cliente" : order.cancelledBy === "admin" ? " · cancelado por el admin" : ""}</span>
              </div>
              <div className={c.cardSide}>
                <span className={c.amount}>{orderMoney(order)}</span>
                <Chip size="small" label={statusLabel(order.status)} style={{ background: statusColor(order.status), color: "#fff" }} />
                <Button size="small" onClick={() => setDetail(order)}>Ver</Button>
                {order.status === "payment_review" && (
                  <>
                    <Button size="small" disabled={busy} onClick={() => { setReview({ order, approve: false }); setReason(""); }}>Rechazar</Button>
                    <Button size="small" variant="contained" color="primary" disabled={busy} onClick={() => { setReview({ order, approve: true }); setReason(""); }}>Aprobar</Button>
                  </>
                )}
                {order.status === "awaiting_payment" && <Button size="small" disabled={busy} style={{ color: "#dc2626" }} onClick={() => askCancel(order)}>Cancelar</Button>}
                {order.status === "manual_activation" && order.channel === "web" && <Button size="small" color="primary" disabled={busy} onClick={() => openActivation(order)}>Completar activación</Button>}
              </div>
            </article>
          ))}
        </div>
        {data.hasMore && (
          <div className={c.more}><Button variant="outlined" disabled={loading} onClick={() => load(page + 1)}>Cargar más</Button></div>
        )}
      </RecordsState>

      <OrderDetail
        order={detail}
        busy={busy}
        onClose={() => setDetail(null)}
        onReview={(order, approve) => { setReview({ order, approve }); setReason(""); }}
        onOpenChat={(order) => openChat({ contactId: order.contactId, name: order.name, number: order.number })}
        onActivate={openActivation}
        onCloseSpecial={setClosingSpecial}
        onCancel={askCancel}
      />

      <Dialog open={!!cancelling} onClose={() => setCancelling(null)} maxWidth="xs" fullWidth classes={{ paper: dlg.paper }}>
        <DialogTitle>Cancelar pedido #{cancelling?.id}</DialogTitle>
        <DialogContent>
          <p>El pedido deja de contar como pendiente y se libera la cuenta o el perfil que tenía reservado. Útil para pedidos de prueba o clientes que desistieron.</p>
          {cancelling?.status === "payment_review" && <p><strong>Tiene un comprobante en revisión:</strong> si el cliente sí pagó, mejor apruébalo o recházalo.</p>}
          <FormControlLabel control={<Checkbox color="primary" checked={notifyCancel} onChange={(e) => setNotifyCancel(e.target.checked)} />} label="Avisar al cliente por su chat" />
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setCancelling(null)}>Volver</Button>
          <Button variant="contained" disabled={busy} style={{ background: "#dc2626", color: "#fff" }} onClick={confirmCancel}>Cancelar pedido</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!review} onClose={() => setReview(null)} maxWidth="sm" fullWidth classes={{ paper: dlg.paper }}>
        <DialogTitle>{review?.approve ? "Aprobar pago" : "Rechazar pago"} · pedido #{review?.order.id}</DialogTitle>
        <DialogContent>
          <p>
            {review?.approve
              ? "Confirma que verificaste el pago. Se entregan los servicios (o se aplica la renovación o la recarga) y el cliente recibe los datos por su chat. Si el pedido requiere activación manual, quedará pendiente de asignación."
              : "El cliente recibe el aviso de que el pago no se pudo validar. Escribe el motivo para que sepa qué corregir."}
          </p>
          {!review?.approve && <TextField fullWidth autoFocus label="Motivo" variant="outlined" value={reason} onChange={(e) => setReason(e.target.value)} />}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setReview(null)}>Cancelar</Button>
          <Button color="primary" variant="contained" disabled={busy} onClick={confirmReview}>{review?.approve ? "Aprobar" : "Rechazar"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!closingSpecial} onClose={() => setClosingSpecial(null)} maxWidth="xs" fullWidth classes={{ paper: dlg.paper }}>
        <DialogTitle>Cerrar pedido especial #{closingSpecial?.id}</DialogTitle>
        <DialogContent><p>Deja de contar como pendiente. Se cierra igual que el cierre automático, marcado como cerrado por el admin; no se le envía nada al cliente.</p></DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setClosingSpecial(null)}>Cancelar</Button>
          <Button color="primary" disabled={busy} onClick={() => run(async () => {
            const { data: res } = await api.post(`/negocio/storefront/orders/${closingSpecial.id}/close-special`);
            toast.success(res.message);
            const id = closingSpecial.id;
            setClosingSpecial(null);
            await refreshOne(id).catch(() => undefined);
            await load(1, true);
          })}>Cerrar pedido</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!activation} onClose={() => setActivation(null)} maxWidth="sm" fullWidth classes={{ paper: dlg.paper }}>
        <DialogTitle>Activar pedido #{activation?.id}</DialogTitle>
        <DialogContent>
          <p>Asigna los datos por unidad. El sistema reutiliza la venta pagada y valida el stock; no registra un segundo cobro.</p>
          {deliveries.map((delivery, index) => {
            const account = accounts.find((a) => Number(a.id) === Number(delivery.accountId));
            const set = (patch) => setDeliveries(deliveries.map((d, i) => (i === index ? { ...d, ...patch } : d)));
            return (
              <div key={index} style={{ margin: "20px 0", display: "grid", gap: 12 }}>
                <strong>{delivery.name} · unidad {index + 1}</strong>
                <TextField select label="Cuenta" variant="outlined" fullWidth value={delivery.accountId} onChange={(e) => set({ accountId: e.target.value, profileId: "" })}>
                  <MenuItem value="">Sin cuenta (si el producto no la requiere)</MenuItem>
                  {activationAccounts(accounts, deliveries, index).map((a) => (
                    <MenuItem key={a.id} value={a.id}><AccountOption account={a} email={a.email || a.code || `Cuenta #${a.id}`} /></MenuItem>
                  ))}
                </TextField>
                {account && !accountIsFullSale(account) && needsSlots(account) && (
                  <TextField select label="Perfil / puesto" variant="outlined" fullWidth value={delivery.profileId} onChange={(e) => set({ profileId: e.target.value })}>
                    <MenuItem value="">Cuenta completa / no aplica</MenuItem>
                    {activationProfiles(account, deliveries, index).map((p) => <MenuItem key={p.id} value={p.id}>{p.name || `Puesto #${p.id}`}</MenuItem>)}
                  </TextField>
                )}
                <TextField label="Código / enlace de activación (si aplica)" variant="outlined" fullWidth value={delivery.deliveryCode} onChange={(e) => set({ deliveryCode: e.target.value })} />
              </div>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setActivation(null)}>Cancelar</Button>
          <Button color="primary" disabled={busy} onClick={() => run(async () => {
            const id = activation.id;
            await api.post(`/negocio/storefront/orders/${id}/activate`, { deliveries });
            setActivation(null);
            toast.success("Pedido activado en la cuenta del cliente.");
            await refreshOne(id).catch(() => undefined);
            await load(1, true);
          })}>Activar servicios</Button>
        </DialogActions>
      </Dialog>
      {chatModal}
    </PageFrame>
  );
}
